import { usePwaUpdate } from './pwa-update.store';
import { BUILD_PROBE_MESSAGE, currentBuildAssets, type BuildProbeReply } from './sw-build-probe';

/**
 * Service worker registration + the "new version ready" decision. Called once
 * from the app bootstrap (production only — the SW is disabled in dev).
 *
 * "Prompt" strategy: when a new worker is WAITING and it is a different build
 * from the one this page runs, a persistent top banner (`<PwaUpdateBanner>`)
 * offers "Update now" — never a surprise reload mid-session (B2B app).
 *
 * Why this no longer uses `virtual:pwa-register` (workbox-window): it announced
 * an update for every waiting worker, including one carrying the build the page
 * ALREADY runs (the app shell is network-first, so the first open after a deploy
 * is already new while the new worker sits in `waiting` behind the old one), it
 * prompted on an "external" install even when that worker activated straight
 * away, and after the first "external" update it stopped listening for later
 * ones. Result: "A new version is ready" with nothing new to get. Here we listen
 * to the registration directly and ask the waiting worker whether it is our
 * build (`sw-build-probe.ts`) before showing anything.
 */

const SW_URL = '/service-worker.js';
/** How often an open tab re-checks the server for a new worker. */
export const UPDATE_CHECK_INTERVAL_MS = 60_000;
/** Waiting worker must answer the same-build probe within this. */
export const PROBE_TIMEOUT_MS = 2_000;
/** After "Update now", reload anyway if the controller hasn't switched by then. */
export const APPLY_FALLBACK_MS = 4_000;

/** The slice of the platform the update flow touches — faked in specs. */
export interface UpdateFlowDeps {
  /** Resolves true when `sw` carries the build this page is running. */
  isSameBuild: (sw: ServiceWorker) => Promise<boolean>;
  /** Show the banner; `apply` activates the waiting worker and reloads. */
  announce: (apply: () => void) => void;
  reload: () => void;
  /** `navigator.serviceWorker` (for `controllerchange`). */
  container: Pick<ServiceWorkerContainer, 'addEventListener'>;
  setTimeout: (fn: () => void, ms: number) => unknown;
}

/**
 * Wire one registration to the banner. Exported for specs; production calls it
 * via {@link setupServiceWorker}.
 */
export function wireUpdateFlow(reg: ServiceWorkerRegistration, deps: UpdateFlowDeps): void {
  const considered = new WeakSet<ServiceWorker>();
  let applying = false;
  let reloaded = false;

  const reloadOnce = () => {
    if (reloaded) return;
    reloaded = true;
    deps.reload();
  };

  // The waiting worker took over (our SKIP_WAITING, or another tab's) — only
  // reload when THIS tab asked for it; never yank a page out from under a user.
  deps.container.addEventListener('controllerchange', () => {
    if (applying) reloadOnce();
  });

  const apply = () => {
    if (applying) return;
    applying = true;
    const waiting = reg.waiting;
    if (!waiting) {
      // Already activated (e.g. by another tab) — just load it.
      reloadOnce();
      return;
    }
    waiting.postMessage({ type: 'SKIP_WAITING' });
    // Belt-and-braces: if `controllerchange` never arrives, still land on the
    // new version instead of sitting on "Updating…" forever. One reload — the
    // reloaded page is the new build (network-first shell), so the probe then
    // says "same build" and the banner can't come back → no loop.
    deps.setTimeout(reloadOnce, APPLY_FALLBACK_MS);
  };

  const consider = (sw: ServiceWorker | null) => {
    if (!sw || considered.has(sw)) return;
    // Only a worker that is actually WAITING needs the user (an install with no
    // active worker, or one that activates straight away, needs nothing).
    if (reg.waiting !== sw || !reg.active) return;
    considered.add(sw);
    void deps
      .isSameBuild(sw)
      .catch(() => false)
      .then((same) => {
        if (same) return; // Already running this build — nothing to offer.
        if (reg.waiting !== sw) return; // Superseded / activated meanwhile.
        deps.announce(apply);
      });
  };

  // A worker left waiting by an earlier visit / another tab.
  consider(reg.waiting);

  reg.addEventListener('updatefound', () => {
    const sw = reg.installing;
    if (!sw) return;
    sw.addEventListener('statechange', () => {
      if (sw.state === 'installed') consider(sw);
    });
  });
}

/** Ask a waiting worker whether its precache holds every asset in `paths`. */
export function probeSameBuild(
  sw: ServiceWorker,
  paths: string[],
  timeoutMs = PROBE_TIMEOUT_MS,
): Promise<boolean> {
  if (paths.length === 0 || typeof MessageChannel === 'undefined') return Promise.resolve(false);
  return new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const timer = setTimeout(() => resolve(false), timeoutMs);
    channel.port1.onmessage = (event: MessageEvent<BuildProbeReply>) => {
      clearTimeout(timer);
      resolve(event.data?.has === true);
    };
    try {
      sw.postMessage({ type: BUILD_PROBE_MESSAGE, paths }, [channel.port2]);
    } catch {
      clearTimeout(timer);
      resolve(false);
    }
  });
}

/** Re-check the server for a new worker on load, on a timer and on focus. */
function startUpdateChecks(reg: ServiceWorkerRegistration): void {
  // Browsers (iOS especially, while the PWA stays open) check on their own lazy
  // schedule; force it so a real deploy surfaces promptly.
  const check = () => {
    if (navigator.onLine !== false) void reg.update().catch(() => {});
  };
  setInterval(check, UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') check();
  });
  window.addEventListener('focus', check);
}

export function setupServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Snapshot now: the assets THIS page booted with (index.html's tags).
  const assets = currentBuildAssets(document, window.location.origin);

  const register = () => {
    navigator.serviceWorker
      .register(SW_URL, { scope: '/' })
      .then((reg) => {
        wireUpdateFlow(reg, {
          isSameBuild: (sw) => probeSameBuild(sw, assets),
          announce: (apply) => usePwaUpdate.getState().announceUpdate(apply),
          reload: () => window.location.reload(),
          container: navigator.serviceWorker,
          setTimeout: (fn, ms) => window.setTimeout(fn, ms),
        });
        startUpdateChecks(reg);
      })
      .catch((err: unknown) => {
        console.error('[pwa] SW registration failed', err);
      });
  };

  // Register after load so the SW's precache download doesn't compete with the
  // app's own first paint (same as the previous workbox-window default).
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}
